import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { CreateService } from '@common/enums';
import { OdataObject } from '@common/models';
import { ODataServiceFactory } from '@common/odata';
import { I18NextPipe } from 'angular-i18next';
import { saveAs } from 'file-saver';
import { DateFormat } from 'mbs-ui-kit';
import { SPACE_REGEX } from 'mbs-ui-kit/utils/constants';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DownloadService {
  private readonly datePipe = inject(DatePipe);
  private readonly http = inject(HttpClient);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly odataFactory = inject(ODataServiceFactory);

  private readonly loadingMap = signal<Map<unknown, boolean>>(new Map());

  getTempLink(guidId: string): Observable<string> {
    return this.http
      .get<OdataObject<string>>(this.odataFactory.CreateService(`Artifacts(${guidId})/GetTempLink`).Query().GetUrl())
      .pipe(map((res) => res?.value));
  }

  saveFile(tempLink: string, guidId: string): void {
    const link = document.createElement('a');

    link.href = this.odataFactory.CreateService(`Artifacts(${guidId})/Download(tempLink=${tempLink})`).Query().GetUrl();
    link.target = '_blank';

    link.click();
    link.remove();
  }

  downloadBare(serviceName: string): Observable<Blob> {
    return this.http.get(this.odataFactory.CreateService(serviceName).Query().GetUrl(), { responseType: 'blob' });
  }

  download(id: string, service: CreateService, query?: string): Observable<Blob> {
    const rawUrl = this.odataFactory.CreateService(service).Get(id).GetUrl() + this.getUrlEnd(service);
    const url = query ? rawUrl + query : rawUrl;

    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(map((res) => res.body));
  }

  private getUrlEnd(service: CreateService): string {
    switch (service) {
      case CreateService.SiteItems:
        return '/GetRaw';
      case CreateService.MailMessages:
        return '/DownloadAttachment';
      default:
        return '/Download';
    }
  }

  generateFileName(path: string): string {
    return `${path}_${this.datePipe.transform(new Date(), DateFormat.short).replace(SPACE_REGEX, '_')}`;
  }

  saveAs(file: Blob, name?: string): void {
    const filename = this.getFileName(name);
    const blob = this.getBlob(file);

    saveAs(blob, filename);
  }

  private getFileName(name: string): string {
    return name || this.i18nPipe.transform('common.noFileName').replace(SPACE_REGEX, '_');
  }

  private getBlob(file: Blob): Blob {
    return new Blob([file], { type: file.type });
  }

  updateLoading(item: unknown, state: boolean): void {
    this.mutateMap((map) => map.set(item, state));
  }

  removeLoading(item: unknown): void {
    this.mutateMap((map) => map.delete(item));
  }

  isLoading(item: unknown): boolean {
    return this.loadingMap().get(item) ?? false;
  }

  loading(item: unknown): Signal<boolean> {
    return computed(() => this.loadingMap().get(item) ?? false);
  }

  private mutateMap(mutator: (map: Map<unknown, boolean>) => void): void {
    this.loadingMap.update((map) => {
      const copy = new Map(map);

      mutator(copy);

      return copy;
    });
  }
}
